import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useSearchParams } from "react-router-dom";
import { TOPICS, TOPIC_SUB_CATEGORIES } from "../../constants";
import { cls } from "../../utils/util";
import { CourseList } from "../../types/klass";
import { trackClassSearchCategoryClicked } from "@/tracking";

const ALL = "ALL";
const DUMMY = "__dummy__";
const DR_YOUNG_GROUP = "drYoungAcademy";

const FeaturedNewBadge = () => (
  <span className="mr-[5px] inline-block h-[5px] w-[5px] shrink-0 rounded-full bg-[#FF228C] align-middle" />
);

export const useKlassCategory = (courses?: CourseList[]) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const topics = searchParams.get("topics");
  const topicCategory = searchParams.get("topicCategory") || ALL;
  const topicSubCategory = searchParams.get("topicSubCategory") || ALL;
  const categoryGroup = searchParams.get("categoryGroup");

  const currentTopic = TOPICS.find((topic) => topic.label.replace(/\s+/g, "") === topics);
  const rawTopicCategoryList = currentTopic ? TOPIC_SUB_CATEGORIES[currentTopic.name] : undefined;

  const presentCategoryLabels = new Set<string>();
  const presentSubCategoryLabels = new Set<string>();
  courses?.forEach((course) => {
    course.groupedBadges?.topicCategory?.forEach((label) => presentCategoryLabels.add(label));
    course.groupedBadges?.topicSubCategory?.forEach((label) => presentSubCategoryLabels.add(label));
  });

  const topicCategoryList = courses
    ? rawTopicCategoryList?.filter((info) => presentCategoryLabels.has(info.label))
    : rawTopicCategoryList;
  const hasCategoryNav = !!topicCategoryList && topicCategoryList.length > 0;

  const hasDyGroup = !!topicCategoryList?.some((d) => d.isDrYoungAcademy);
  const visibleTopicCategoryList =
    categoryGroup === DR_YOUNG_GROUP
      ? topicCategoryList?.filter((d) => d.isDrYoungAcademy)
      : topicCategoryList;

  const selectedTopicCategory = topicCategoryList?.find((info) => info.name === topicCategory);
  const selectedTopicSubCategoryChildren = selectedTopicCategory?.children ?? [];
  const topicSubCategoryList = courses
    ? selectedTopicSubCategoryChildren.filter((child) => presentSubCategoryLabels.has(child.label))
    : selectedTopicSubCategoryChildren;

  const selectGroup = (g: string | null) => {
    setSearchParams((prev) => {
      prev.delete("topicCategory");
      prev.delete("topicSubCategory");
      prev.delete("q");
      if (!g) prev.delete("categoryGroup");
      else prev.set("categoryGroup", g);
      return prev;
    });
  };

  const selectTopicCategory = (name: string) => {
    if (name !== ALL) {
      trackClassSearchCategoryClicked({
        depth: "category",
        topic: currentTopic?.label,
        category: rawTopicCategoryList?.find((info) => info.name === name)?.label ?? name,
      });
    }
    setSearchParams((prev) => {
      prev.delete("topicSubCategory");
      prev.delete("q");
      if (name === ALL) prev.delete("topicCategory");
      else prev.set("topicCategory", name);
      return prev;
    });
  };

  const selectTopicSubCategory = (name: string) => {
    if (name !== ALL) {
      trackClassSearchCategoryClicked({
        depth: "subcategory",
        topic: currentTopic?.label,
        category: selectedTopicCategory?.label,
        subcategory:
          selectedTopicCategory?.children?.find((child) => child.name === name)?.label ?? name,
      });
    }
    setSearchParams((prev) => {
      prev.delete("q");
      if (name === ALL) prev.delete("topicSubCategory");
      else prev.set("topicSubCategory", name);
      return prev;
    });
  };

  return {
    currentTopic,
    topicCategoryList,
    visibleTopicCategoryList,
    topicSubCategoryList,
    selectedTopicCategoryName: topicCategory,
    selectedTopicSubCategoryName: topicSubCategory,
    categoryGroup,
    hasDyGroup,
    selectGroup,
    hasCategoryNav,
    selectTopicCategory,
    selectTopicSubCategory,
    ALL,
  };
};

type CategoryNav = ReturnType<typeof useKlassCategory>;

export const KlassCategorySidebar = ({ nav }: { nav: CategoryNav }) => {
  const {
    currentTopic,
    topicCategoryList,
    selectedTopicCategoryName,
    selectedTopicSubCategoryName,
    selectTopicCategory,
    selectTopicSubCategory,
    ALL,
  } = nav;
  if (!currentTopic || !topicCategoryList) return null;

  return (
    <div className="w-full">
      <button
        type="button"
        className={cls(
          "w-full rounded-t-[5px] border-b border-solid border-gray-900 p-[10px]",
          "text-start text-[18px] font-bold leading-normal tracking-[-0.32px]",
          "hover:bg-secondary-100",
        )}
        onClick={() => selectTopicCategory(ALL)}
      >
        {currentTopic.label}
      </button>
      <ul>
        {topicCategoryList.map((info) => {
          const isExpanded = selectedTopicCategoryName === info.name;
          const hasChildren = (info.children?.length ?? 0) > 0;
          return (
            <li key={info.name}>
              <button
                type="button"
                onClick={() => selectTopicCategory(isExpanded ? ALL : info.name)}
                className={cls(
                  "flex w-full items-center justify-between gap-1 rounded-[5px] p-[8px] text-left",
                  "text-[15px] leading-[21px] tracking-[0.28px]",
                  "hover:bg-secondary-100 hover:text-secondary-900",
                  isExpanded ? "font-semibold text-secondary-900" : "text-secondary-700",
                )}
              >
                <span className="flex items-center">
                  {info.isDrYoungAcademy && <FeaturedNewBadge />}
                  {info.label}
                </span>
                {hasChildren && (
                  <i
                    className={cls(
                      "text-[16px] text-secondary-500",
                      isExpanded ? "ii ii-chevron-up" : "ii ii-chevron-down",
                    )}
                  />
                )}
              </button>
              {isExpanded && hasChildren && (
                <ul className="mb-1 border-l border-solid border-secondary-200 pl-[8px]">
                  <li>
                    <button
                      type="button"
                      onClick={() => selectTopicSubCategory(ALL)}
                      className={cls(
                        "w-full rounded-[5px] py-[6px] pl-[12px] text-left text-[14px] leading-[20px]",
                        "hover:bg-secondary-100",
                        selectedTopicSubCategoryName === ALL
                          ? "font-bold text-primary-700"
                          : "text-secondary-600",
                      )}
                    >
                      전체
                    </button>
                  </li>
                  {info.children?.map((child) => (
                    <li key={child.name}>
                      <button
                        type="button"
                        onClick={() => selectTopicSubCategory(child.name)}
                        className={cls(
                          "w-full rounded-[5px] py-[6px] pl-[12px] text-left text-[14px] leading-[20px]",
                          "hover:bg-secondary-100",
                          selectedTopicSubCategoryName === child.name
                            ? "font-bold text-primary-700"
                            : "text-secondary-600",
                        )}
                      >
                        {child.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export const KlassCategoryMobileBar = ({ nav }: { nav: CategoryNav }) => {
  const {
    currentTopic,
    topicCategoryList,
    visibleTopicCategoryList,
    topicSubCategoryList,
    selectedTopicCategoryName,
    selectedTopicSubCategoryName,
    categoryGroup,
    hasDyGroup,
    selectGroup,
    selectTopicCategory,
    selectTopicSubCategory,
    ALL,
  } = nav;
  const [isTopicCategoryOpen, setIsTopicCategoryOpen] = useState(false);
  const [dropdownLeft, setDropdownLeft] = useState(16);
  const [dropdownTop, setDropdownTop] = useState(100);
  const containerRef = useRef<HTMLDivElement>(null);
  const topicCategoryBtnRef = useRef<HTMLButtonElement>(null);
  const breadcrumbRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsTopicCategoryOpen(false);
      }
    };
    if (isTopicCategoryOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isTopicCategoryOpen]);

  const toggleTopicCategoryOpen = () => {
    if (topicCategoryBtnRef.current) setDropdownLeft(topicCategoryBtnRef.current.offsetLeft);
    if (breadcrumbRef.current)
      setDropdownTop(breadcrumbRef.current.offsetTop + breadcrumbRef.current.offsetHeight);
    setIsTopicCategoryOpen((prev) => !prev);
  };

  if (!currentTopic || !topicCategoryList) return null;

  const selectedTopicCategory = topicCategoryList.find(
    (info) => info.name === selectedTopicCategoryName,
  );
  const hasTopicCategorySelected = selectedTopicCategoryName !== ALL && !!selectedTopicCategory;
  const selectedTopicSubCategory = topicSubCategoryList.find(
    (info) => info.name === selectedTopicSubCategoryName,
  );

  const showTopicSubCategory = hasTopicCategorySelected && topicSubCategoryList.length > 0;
  const baseGridList = showTopicSubCategory
    ? [{ name: ALL, label: "전체" }, ...topicSubCategoryList]
    : [{ name: ALL, label: "전체" }, ...(visibleTopicCategoryList ?? [])];
  const gridList =
    baseGridList.length % 2 > 0
      ? [...baseGridList, { name: `${DUMMY}-0`, label: "" }]
      : baseGridList;

  const handleClickGridItem = (name: string) => {
    if (showTopicSubCategory) selectTopicSubCategory(name);
    else selectTopicCategory(name);
    setIsTopicCategoryOpen(false);
  };

  const isGridItemSelected = (name: string) =>
    showTopicSubCategory
      ? selectedTopicSubCategoryName === name
      : selectedTopicCategoryName === name;

  return (
    <div className="relative flex flex-col" ref={containerRef}>
      <div ref={breadcrumbRef} className="flex items-center gap-2 p-3 lg:px-4 lg:pt-4 xl:px-0">
        {hasDyGroup && (
          <div className="inline-flex shrink-0 gap-1 rounded-[8px] bg-secondary-100 p-[2px] text-[13px] font-semibold lg:p-[3px] lg:text-[14px]">
            {(
              [
                [null, "피부미용 카테고리"],
                [DR_YOUNG_GROUP, "최재영 카테고리"],
              ] as const
            ).map(([value, label]) => {
              const active =
                value === DR_YOUNG_GROUP
                  ? categoryGroup === DR_YOUNG_GROUP
                  : categoryGroup !== DR_YOUNG_GROUP;
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => {
                    selectGroup(value);
                    setIsTopicCategoryOpen(false);
                  }}
                  className={cls(
                    "whitespace-nowrap rounded-[5px] px-[10px] py-[5px] lg:rounded-[6px] lg:px-4 lg:py-[6px]",
                    active ? "bg-white text-primary-700 shadow-sm" : "text-secondary-600",
                  )}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
        {!hasDyGroup && (
          <div className="inline-flex shrink-0 gap-1 rounded-[8px] bg-secondary-100 p-[2px] text-[13px] font-semibold lg:p-[3px] lg:text-[14px]">
            <button
              type="button"
              onClick={() => {
                selectTopicCategory(ALL);
                setIsTopicCategoryOpen(false);
              }}
              className="whitespace-nowrap rounded-[5px] bg-white px-[10px] py-[5px] text-primary-700 shadow-sm lg:rounded-[6px] lg:px-4 lg:py-[6px]"
            >
              {currentTopic.label} 카테고리
            </button>
          </div>
        )}
        <div className="flex h-[16px] min-w-0 items-center gap-[4px] overflow-x-auto overflow-y-hidden whitespace-nowrap text-[13px] scroll_none lg:text-[14px]">
          {hasTopicCategorySelected ? (
            <button
              ref={topicCategoryBtnRef}
              type="button"
              className={cls(
                "flex items-center font-medium leading-normal text-medi-text-tertiary",
                selectedTopicSubCategoryName === ALL &&
                  !isTopicCategoryOpen &&
                  "!text-medi-text-primary",
                selectedTopicSubCategoryName !== ALL &&
                  isTopicCategoryOpen &&
                  "!font-bold !text-medi-text-secondary",
                selectedTopicSubCategoryName === ALL &&
                  isTopicCategoryOpen &&
                  "!font-bold !text-medi-text-primary",
              )}
              onClick={toggleTopicCategoryOpen}
            >
              {selectedTopicCategory.label}
              <i className="ii ii-caret-down ii-weight-600 text-[24px] text-secondary-900" />
            </button>
          ) : null}
          {hasTopicCategorySelected &&
            selectedTopicSubCategory &&
            selectedTopicSubCategoryName !== ALL && (
              <>
                <i className="ii ii-chevron-right ii-weight-600 flex h-[16px] items-center justify-center text-[16px] text-secondary-500" />
                <span className="font-medium leading-normal text-medi-text-primary">
                  {selectedTopicSubCategory.label}
                </span>
              </>
            )}
        </div>
      </div>

      <div className="border-b-[8px] border-solid border-secondary-100 lg:hidden">
        <div className="flex flex-wrap border-l border-t border-solid border-secondary-200">
          {gridList.map((info) => {
            const isDummy = info.name.startsWith(DUMMY);
            return (
              <button
                key={info.name}
                type="button"
                disabled={isDummy}
                className={cls(
                  "flex w-1/2 items-center border-b border-r border-solid border-secondary-200 px-4 py-[8px] text-left",
                  "text-[14px] leading-[20px] tracking-[0.1px] text-medi-text-primary",
                  isGridItemSelected(info.name) && "bg-primary-100 font-bold !text-primary-700",
                  isDummy && "cursor-default",
                )}
                onClick={() => !isDummy && handleClickGridItem(info.name)}
              >
                {"isDrYoungAcademy" in info && info.isDrYoungAcademy ? <FeaturedNewBadge /> : null}
                <span className="min-w-0 break-keep">{info.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="hidden lg:block lg:px-4 xl:px-0">
        <ul className="box-border flex w-fit flex-wrap items-center overflow-hidden rounded-md border border-solid border-secondary-200">
          {baseGridList.map((info) => (
            <li
              key={info.name}
              className={cls(
                "relative flex cursor-pointer items-center px-[12px] py-[10px] text-[14px] leading-[17px] tracking-[-0.35px]",
                "after:absolute after:right-0 after:top-1/2 after:block after:h-[16px] after:w-[1px] after:-translate-y-1/2 after:bg-secondary-300 after:content-[''] last:after:hidden",
                isGridItemSelected(info.name)
                  ? "bg-primary-100 font-semibold text-primary-700 hover:bg-primary-200"
                  : "text-secondary-600 hover:bg-secondary-100",
              )}
              onClick={() => handleClickGridItem(info.name)}
            >
              {"isDrYoungAcademy" in info && info.isDrYoungAcademy ? <FeaturedNewBadge /> : null}
              {info.label}
            </li>
          ))}
        </ul>
      </div>

      {isTopicCategoryOpen && (
        <div
          style={{ top: dropdownTop }}
          className="absolute left-0 right-0 z-[999] h-screen bg-black/40 backdrop-blur-sm lg:hidden"
          onClick={() => setIsTopicCategoryOpen(false)}
        />
      )}

      {isTopicCategoryOpen && (
        <div
          style={
            {
              top: dropdownTop,
              "--topic-category-dropdown-left": `${dropdownLeft}px`,
            } as CSSProperties
          }
          className={cls(
            "absolute z-[1000] p-[10px]",
            "grid grid-cols-2 gap-[4px] lg:grid-cols-3",
            "left-0 right-0 w-full lg:left-[var(--topic-category-dropdown-left)] lg:right-auto lg:w-[502px]",
            "bg-white lg:rounded-[6px]",
            "max-h-[473px] overflow-y-auto",
            "shadow-[0px_0px_0px_1px_rgba(212,215,223,0.50),0px_6px_16px_0px_rgba(37,40,46,0.10),0px_6px_16px_-4px_rgba(37,40,46,0.10)]",
          )}
        >
          {topicCategoryList.map((info) => (
            <button
              key={info.name}
              type="button"
              className={cls(
                "flex items-center rounded-[4px] p-[8px] text-left text-[14px] leading-[20px]",
                selectedTopicCategoryName === info.name
                  ? "bg-primary-100 font-bold text-primary-700 lg:hover:bg-primary-200"
                  : "font-normal text-medi-text-secondary lg:hover:bg-secondary-100",
              )}
              onClick={(e) => {
                e.stopPropagation();
                selectTopicCategory(info.name);
                setIsTopicCategoryOpen(false);
              }}
            >
              {info.isDrYoungAcademy && <FeaturedNewBadge />}
              {info.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
